import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import logger from "../../utils/logger";
import { Database, RefreshCw, ToggleLeft, ToggleRight, ExternalLink } from "lucide-react";

const API = `${window.location.origin}/api`;

export function DataSourcesTab() {
  const [sources, setSources] = useState([]);
  const [activeCount, setActiveCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(null);

  const fetchSources = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/data-sources`, { withCredentials: true });
      setSources(data.sources || []);
      setActiveCount(data.active_count || 0);
    } catch (err) {
      logger.error("Failed to fetch data sources:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchSources(); }, [fetchSources]);

  const toggleSource = async (sourceId, currentEnabled) => {
    try {
      await axios.put(`${API}/data-sources/${sourceId}`, { enabled: !currentEnabled }, { withCredentials: true });
      setSources(prev => prev.map(s => s.id === sourceId ? { ...s, enabled: !s.enabled } : s));
      setActiveCount(prev => currentEnabled ? prev - 1 : prev + 1);
    } catch (err) {
      logger.error("Failed to toggle source:", err);
    }
  };

  const syncSource = async (sourceId) => {
    setSyncing(sourceId);
    try {
      const { data } = await axios.post(`${API}/data-sources/${sourceId}/sync`, {}, { withCredentials: true });
      setSources(prev => prev.map(s => s.id === sourceId ? { ...s, last_synced: data.last_synced, records: data.records } : s));
    } catch (err) {
      logger.error("Failed to sync source:", err);
    } finally {
      setSyncing(null);
    }
  };

  function timeAgo(iso) {
    if (!iso) return "Never";
    const diff = Date.now() - new Date(iso).getTime();
    const hrs = Math.floor(diff / 3600000);
    if (hrs < 1) return "Just now";
    if (hrs < 24) return `${hrs}h ago`;
    return `${Math.floor(hrs / 24)}d ago`;
  }

  if (loading) return <div style={{ padding: "40px", textAlign: "center", color: "#94a3b8" }}>Loading data sources...</div>;

  return (
    <div data-testid="data-sources-tab">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h2 style={{ fontSize: "15px", fontWeight: 700, color: "#f1f5f9" }}>India Data Sources</h2>
          <p style={{ fontSize: "12px", color: "#475569" }}>{activeCount} of {sources.length} sources active</p>
        </div>
        <div style={{
          fontSize: "10px", fontWeight: 700, padding: "4px 12px", borderRadius: "99px",
          background: "rgba(37,99,235,0.12)", color: "#60a5fa",
        }}>
          {activeCount}/{sources.length} ACTIVE
        </div>
      </div>

      <div className="space-y-3">
        {sources.map((src) => (
          <div key={src.id} data-testid={`source-${src.id}`} style={{
            background: "#0d1117", border: `1px solid ${src.enabled ? "#1e2530" : "#1a1a24"}`,
            borderRadius: "10px", padding: "16px", opacity: src.enabled ? 1 : 0.6,
            transition: "all 0.2s",
          }}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3" style={{ flex: 1 }}>
                <div style={{
                  width: "36px", height: "36px", borderRadius: "8px",
                  background: src.enabled ? "rgba(16,185,129,0.1)" : "rgba(71,85,105,0.1)",
                  display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
                }}>
                  <Database style={{ width: "16px", height: "16px", color: src.enabled ? "#10b981" : "#475569" }} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: "13px", fontWeight: 600, color: "#f1f5f9" }}>{src.name}</div>
                  <div style={{ fontSize: "11px", color: "#475569" }}>{src.provider}</div>
                </div>
              </div>

              <div className="flex items-center gap-4">
                <div style={{ textAlign: "right" }}>
                  <div style={{ fontSize: "12px", fontWeight: 700, color: "#f1f5f9" }}>
                    {src.records?.toLocaleString("en-IN")}
                  </div>
                  <div style={{ fontSize: "9px", color: "#475569", textTransform: "uppercase" }}>Records</div>
                </div>
                <div style={{ textAlign: "right", minWidth: "60px" }}>
                  <div style={{ fontSize: "11px", color: "#94a3b8" }}>{timeAgo(src.last_synced)}</div>
                  <div style={{ fontSize: "9px", color: "#475569" }}>Last Sync</div>
                </div>
                <button
                  onClick={() => syncSource(src.id)}
                  disabled={syncing === src.id || !src.enabled}
                  data-testid={`sync-${src.id}`}
                  style={{
                    background: "rgba(37,99,235,0.08)", border: "1px solid rgba(37,99,235,0.2)",
                    borderRadius: "6px", padding: "5px 10px", cursor: src.enabled ? "pointer" : "not-allowed",
                    color: "#60a5fa", fontSize: "10px", fontWeight: 600,
                    display: "flex", alignItems: "center", gap: "4px",
                    opacity: syncing === src.id ? 0.5 : 1,
                  }}
                >
                  <RefreshCw style={{ width: "11px", height: "11px", animation: syncing === src.id ? "spin 1s linear infinite" : "none" }} />
                  Sync
                </button>
                <button
                  onClick={() => toggleSource(src.id, src.enabled)}
                  data-testid={`toggle-${src.id}`}
                  style={{ background: "transparent", border: "none", cursor: "pointer", padding: "2px" }}
                >
                  {src.enabled
                    ? <ToggleRight style={{ width: "28px", height: "28px", color: "#10b981" }} />
                    : <ToggleLeft style={{ width: "28px", height: "28px", color: "#475569" }} />
                  }
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
